import React, { useState, useEffect } from 'react';
import { authService } from '../services/api';

function OrderReports() {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    userId: '',
    status: '',
    productName: ''
  });

  // Weekly cost data for graph
  const [weeklyData, setWeeklyData] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    applyFilters();
    calculateWeeklyCosts();
  }, [filters, orders]);

  const loadData = async () => {
    try {
      // Load orders
      const ordersResponse = await fetch('https://b1.envicblue.co.uk/api/orders.php?action=all', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('ward_stock_token')}`
        }
      });
      const ordersData = await ordersResponse.json();
      if (ordersData.success) {
        setOrders(ordersData.data);
        setFilteredOrders(ordersData.data);
      }

      // Load users
      const usersResponse = await fetch('https://b1.envicblue.co.uk/api/users.php', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('ward_stock_token')}`
        }
      });
      const usersData = await usersResponse.json();
      if (usersData.success) {
        setUsers(usersData.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...orders];

    if (filters.startDate) {
      filtered = filtered.filter(o => new Date(o.order_date) >= new Date(filters.startDate));
    }
    if (filters.endDate) {
      filtered = filtered.filter(o => new Date(o.order_date) <= new Date(filters.endDate));
    }
    if (filters.userId) {
      filtered = filtered.filter(o => o.user_id == filters.userId);
    }
    if (filters.status) {
      filtered = filtered.filter(o => o.status === filters.status);
    }
    if (filters.productName) {
      filtered = filtered.filter(o => 
        o.product_name.toLowerCase().includes(filters.productName.toLowerCase())
      );
    }

    setFilteredOrders(filtered);
  };

  const calculateWeeklyCosts = () => {
    const weekMap = {};
    
    orders.forEach(order => {
      const date = new Date(order.order_date);
      const weekNum = getWeekNumber(date);
      const year = date.getFullYear();
      const key = `${year}-W${weekNum}`;
      
      if (!weekMap[key]) {
        weekMap[key] = { week: key, total: 0, count: 0 };
      }
      
      const cost = parseFloat(order.price || 0) * parseInt(order.quantity || 0);
      weekMap[key].total += cost;
      weekMap[key].count += 1;
    });

    const weeklyArray = Object.values(weekMap).sort((a, b) => a.week.localeCompare(b.week));
    setWeeklyData(weeklyArray.slice(-12)); // Last 12 weeks
  };

  const getWeekNumber = (date) => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(),0,1));
    return Math.ceil((((d - yearStart) / 86400000) + 1)/7);
  };

  const getTotalCost = () => {
    return filteredOrders.reduce((sum, order) => {
      return sum + (parseFloat(order.price || 0) * parseInt(order.quantity || 0));
    }, 0).toFixed(2);
  };

  const maxWeeklyCost = Math.max(...weeklyData.map(w => w.total), 1);

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center' }}>Loading...</div>;
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      <h1 style={{ fontSize: '28px', marginBottom: '10px' }}>📊 Order Reports & Analytics</h1>
      <p style={{ color: '#666', marginBottom: '30px' }}>View and analyze order data</p>

      {/* Filters */}
      <div style={{
        background: 'white',
        padding: '20px',
        borderRadius: '12px',
        marginBottom: '30px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
      }}>
        <h3 style={{ marginBottom: '15px', fontSize: '18px' }}>🔍 Filters</h3>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '15px'
        }}>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '600' }}>
              Start Date
            </label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters({...filters, startDate: e.target.value})}
              style={{
                width: '100%',
                padding: '8px',
                border: '2px solid #e0e0e0',
                borderRadius: '6px'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '600' }}>
              End Date
            </label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters({...filters, endDate: e.target.value})}
              style={{
                width: '100%',
                padding: '8px',
                border: '2px solid #e0e0e0',
                borderRadius: '6px'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '600' }}>
              Staff Member
            </label>
            <select
              value={filters.userId}
              onChange={(e) => setFilters({...filters, userId: e.target.value})}
              style={{
                width: '100%',
                padding: '8px',
                border: '2px solid #e0e0e0',
                borderRadius: '6px'
              }}
            >
              <option value="">All Staff</option>
              {users.map(user => (
                <option key={user.user_id} value={user.user_id}>
                  {user.full_name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '600' }}>
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({...filters, status: e.target.value})}
              style={{
                width: '100%',
                padding: '8px',
                border: '2px solid #e0e0e0',
                borderRadius: '6px'
              }}
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '600' }}>
              Product Name
            </label>
            <input
              type="text"
              placeholder="Search product..."
              value={filters.productName}
              onChange={(e) => setFilters({...filters, productName: e.target.value})}
              style={{
                width: '100%',
                padding: '8px',
                border: '2px solid #e0e0e0',
                borderRadius: '6px'
              }}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button
              onClick={() => setFilters({
                startDate: '',
                endDate: '',
                userId: '',
                status: '',
                productName: ''
              })}
              style={{
                width: '100%',
                padding: '8px',
                background: '#f0f0f0',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Weekly Cost Graph */}
      <div style={{
        background: 'white',
        padding: '25px',
        borderRadius: '12px',
        marginBottom: '30px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
      }}>
        <h3 style={{ marginBottom: '20px', fontSize: '18px' }}>📈 Weekly Cost Analysis (Last 12 Weeks)</h3>
        <div style={{ position: 'relative', height: '300px' }}>
          {/* Y-axis */}
          <div style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 30,
            width: '60px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            fontSize: '12px',
            color: '#666'
          }}>
            <div>£{maxWeeklyCost.toFixed(0)}</div>
            <div>£{(maxWeeklyCost * 0.75).toFixed(0)}</div>
            <div>£{(maxWeeklyCost * 0.5).toFixed(0)}</div>
            <div>£{(maxWeeklyCost * 0.25).toFixed(0)}</div>
            <div>£0</div>
          </div>

          {/* Graph area */}
          <div style={{
            position: 'absolute',
            left: '70px',
            right: 0,
            top: 0,
            bottom: 30,
            borderLeft: '2px solid #e0e0e0',
            borderBottom: '2px solid #e0e0e0',
            display: 'flex',
            alignItems: 'flex-end',
            gap: '10px',
            padding: '10px'
          }}>
            {weeklyData.map((week, index) => {
              const height = (week.total / maxWeeklyCost) * 100;
              return (
                <div key={week.week} style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '5px'
                }}>
                  <div style={{
                    width: '100%',
                    height: `${height}%`,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: '4px 4px 0 0',
                    position: 'relative',
                    cursor: 'pointer',
                    transition: 'opacity 0.2s'
                  }}
                  title={`Week ${week.week}\nTotal: £${week.total.toFixed(2)}\nOrders: ${week.count}`}
                  >
                    <div style={{
                      position: 'absolute',
                      top: '-25px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      fontSize: '11px',
                      fontWeight: '600',
                      color: '#667eea',
                      whiteSpace: 'nowrap'
                    }}>
                      £{week.total.toFixed(0)}
                    </div>
                  </div>
                  <div style={{
                    fontSize: '10px',
                    color: '#999',
                    transform: 'rotate(-45deg)',
                    marginTop: '15px'
                  }}>
                    {week.week}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '20px',
        marginBottom: '30px'
      }}>
        <div style={{
          background: 'white',
          padding: '20px',
          borderRadius: '12px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>Total Orders</div>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#667eea' }}>
            {filteredOrders.length}
          </div>
        </div>

        <div style={{
          background: 'white',
          padding: '20px',
          borderRadius: '12px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>Total Cost</div>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#10b981' }}>
            £{getTotalCost()}
          </div>
        </div>

        <div style={{
          background: 'white',
          padding: '20px',
          borderRadius: '12px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>Avg Order Value</div>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#f59e0b' }}>
            £{filteredOrders.length > 0 ? (getTotalCost() / filteredOrders.length).toFixed(2) : '0.00'}
          </div>
        </div>
      </div>

      {/* Orders Table */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>Date</th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>Product</th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>Staff</th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>Quantity</th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>Cost</th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map(order => (
                <tr key={order.order_id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '12px', fontSize: '14px' }}>
                    {new Date(order.order_date).toLocaleDateString()}
                  </td>
                  <td style={{ padding: '12px', fontSize: '14px' }}>{order.product_name}</td>
                  <td style={{ padding: '12px', fontSize: '14px' }}>{order.full_name}</td>
                  <td style={{ padding: '12px', fontSize: '14px' }}>{order.quantity}</td>
                  <td style={{ padding: '12px', fontSize: '14px', fontWeight: '600' }}>
                    £{(parseFloat(order.price) * parseInt(order.quantity)).toFixed(2)}
                  </td>
                  <td style={{ padding: '12px' }}>
                    <span style={{
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '600',
                      background: order.status === 'approved' ? '#d1fae5' : 
                                 order.status === 'rejected' ? '#fee2e2' : '#fef3c7',
                      color: order.status === 'approved' ? '#065f46' :
                             order.status === 'rejected' ? '#991b1b' : '#92400e'
                    }}>
                      {order.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default OrderReports;
